import React, { useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon
} from '@mui/icons-material';
import DocumentUploadField from './DocumentUploadField';

/**
 * MultipleDocumentUpload - A component for handling multiple document uploads per document type
 * 
 * @param {Object} props - Component props
 * @param {string} props.documentType - Type of document being uploaded
 * @param {string} props.label - Label for the document field
 * @param {Function} props.onFileSelect - Function to handle file selection
 * @param {Array} props.files - Array of file objects for this document type
 * @param {Array} props.previewUrls - Array of preview URLs for the files
 * @param {Object} props.processingLock - Processing lock status for this document type
 * @param {Object} props.processingStatus - Status of document processing for each file
 * @param {Function} props.onRemove - Function to handle file removal
 * @param {Function} props.onView - Function to handle file viewing
 * @param {string} props.error - Error message if any
 * @param {Array} props.acceptedTypes - Array of accepted file types
 * @param {string} props.description - Description text for the upload field
 * @param {boolean} props.required - Whether the field is required
 * @param {Function} props.onAddMore - Function to handle adding more document upload fields
 */
const MultipleDocumentUpload = ({
  documentType,
  label,
  onFileSelect,
  files = [],
  previewUrls = [],
  processingLock = {},
  processingStatus = {},
  onRemove,
  onView,
  error,
  acceptedTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
  description = 'Upload document',
  required = false,
  onAddMore,
  showPreviewImage = false,
  fileNameFontSize = '0.95rem'
}) => {
  // Hidden input ref and pending index for Add More flow
  const addMoreInputRef = useRef(null);
  const pendingIndexRef = useRef(0);

  // Check if there are any files for this document type
  const hasFiles = files && files.length > 0;
  
  // Get the number of files for this document type
  const fileCount = files ? files.length : 0;
  const nextIndex = fileCount;

  const handleAddMoreClick = () => {
    pendingIndexRef.current = nextIndex;
    if (addMoreInputRef.current) {
      // reset value so selecting the same file triggers change
      addMoreInputRef.current.value = '';
      addMoreInputRef.current.click();
    }
  };

  const handleAddMoreChange = (e) => {
    // delegate to parent with the computed next index
    if (typeof onFileSelect === 'function') {
      onFileSelect(e, documentType, pendingIndexRef.current);
    }
  };
  
  // Determine if first slot is currently processing (key `${documentType}-0`)
  const isProcessingFirst = !!processingLock?.[`${documentType}-0`];

  return (
    <Paper elevation={0} sx={{ mb: 1.5, p: 1, border: '1px solid #ececec', borderRadius: 1 }}>
      {/* Header with label and Add More button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.95rem' }}>
          {label}
          {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
        
        {(() => {
          // Allow Add More after first item is present or clearly uploaded
          const first = hasFiles ? files[0] : null;
          const firstCompleted = !!(first && (
            first.filePath ||
            first.preview ||
            first.file ||
            first.name ||
            first.fileName ||
            first.uploadStatus === 'UPLOADED' ||
            first.uploadStatus === 'COMPLETED' ||
            first.uploadStatus === 'success'
          ));
          return hasFiles && firstCompleted;
        })() && (
          <Tooltip title="Add another document">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddMoreClick}
              sx={{ textTransform: 'none', fontWeight: 600, '&.Mui-disabled': { color: 'text.secondary', borderColor: 'divider', opacity: 1 } }}
            >
              Upload next
            </Button>
          </Tooltip>
        )}
      </Box>
      
      {/* Removed redundant description to reduce vertical space */}
      
      {/* Display existing documents */}
      {hasFiles ? (
        <Box sx={{ mb: 1 }}>
          {files.map((file, index) => (
            <Box key={`${documentType}-${index}`} sx={{ mb: 1 }}>
              {index > 0 && <Divider sx={{ my: 1 }} />}
              
              {/* Document label with index if more than one */}
              {fileCount > 1 && (
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {label} {index === 0 ? '(Primary)' : `#${index + 1}`}
                </Typography>
              )}
              
              <DocumentUploadField
                documentType={`${documentType}-${index}`}
                label=""
                onFileSelect={(e) => onFileSelect(e, documentType, index)}
                file={file}
                previewUrl={previewUrls[index]}
                isProcessing={processingLock[`${documentType}-${index}`]}
                processingStatus={processingStatus[`${documentType}-${index}`]}
                onRemove={() => onRemove(documentType, index)}
                onView={() => onView(documentType, index)}
                error={error}
                acceptedTypes={acceptedTypes}
                description=""
                required={false}
                customStyles={{
                  container: { p: 0, border: 'none' }
                }}
                showPreviewImage={showPreviewImage}
                fileNameFontSize={fileNameFontSize}
              />
            </Box>
          ))}
        </Box>
      ) : (
        // No documents yet, show upload button
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 1, border: '1px dashed #cfcfcf', borderRadius: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            size="small"
            color="primary"
            disabled={isProcessingFirst}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': { borderColor: 'primary.dark' },
              '&.Mui-disabled': { color: 'text.secondary', borderColor: 'divider', opacity: 1 }
            }}
          >
            Upload{isProcessingFirst ? '...' : ''}
            <input
              type="file"
              hidden
              onChange={(e) => onFileSelect(e, documentType, 0)}
              accept={acceptedTypes.join(',')}
            />
            {isProcessingFirst && (
              <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                {/* Reuse the same spinner pattern as ticket creation */}
                <CircularProgress size={16} />
              </Box>
            )}
          </Button>
        </Box>
      )}

      {/* Hidden input for Add More flow */}
      <input
        ref={addMoreInputRef}
        type="file"
        style={{ display: 'none' }}
        accept={acceptedTypes.join(',')}
        onChange={handleAddMoreChange}
      />
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default MultipleDocumentUpload;
